
import {Component, Prop, Vue} from 'vue-property-decorator';
import ServiceContainer from '@flashpointbv/solar-service-container';
import VueI18n from 'vue-i18n';
import TranslateResult = VueI18n.TranslateResult;
import {SolarDataLayer} from "../../../shared/SolarDataLayer";

@Component({
  name: 'catalog-product-configuration',
  components: ServiceContainer().getComponents(),
})
export default class extends Vue {
  $refs!: {
    addToCart: HTMLElement;
  };

  @Prop() private readonly productData: Array<object>;
  @Prop() private readonly selectedProduct: object;
  @Prop({default: 1}) private qtyIncrement: number;
  @Prop() private readonly customerGroupId: number;
  @Prop() private recommendation: object;

  private qty: number = 1;
  private addToCartIsLoading: boolean = false;
  private hasAddToCartSuccess: boolean = false;
  private hasAddToCartSuccessMessage: TranslateResult = '';
  private hasAddToCartError: boolean = false;
  private addToCartErrorMessage: TranslateResult = '';

  private get selectedOptions(): Array<object> {
    return this.$store.getters['CatalogConfigurableProduct/selectedOptions'];
  }

  private get selectedProducts(): Array<object> {
    return this.$store.getters['CatalogConfigurableProduct/selectedProducts'];
  }

  private get isSaleable(): boolean {
    return this.$store.getters['CatalogProduct/isSaleable'] && (this.productData['in_stock'] || ! this.productData['manage_stock']);
  }

  private get showTierPrices(): boolean {
    return 'configurable' === this.productData['product_type'] && this.selectedProducts[0].hasOwnProperty(`tier_price_${this.customerGroupId}`);
  }

  private get activeStock(): number {
    if (this.productData['product_type'] === 'configurable') {
      return this.selectedProducts[0]['qty'];
    }

    return this.productData['qty'];
  }

  private get isBackorder(): boolean {
    if (this.productData['product_type'] === 'configurable') {
      return !! this.selectedProducts[0]['backorders'];
    }

    return !! this.productData['backorders'];
  }

  private get stockMessage(): TranslateResult {
    if (this.isBackorder) {
      return this.$t('In stock');
    }

    if (this.activeStock < 5) {
      return this.$t('Only {qty} left in stock', {qty: this.activeStock});
    }

    return this.$t('{qty} in stock', {qty: this.activeStock});
  }

  private get stockClass(): string {
    if (this.isBackorder) {
      return '';
    }

    if (this.activeStock < 5) {
      return '--low';
    }

    if (this.activeStock < 15) {
      return '--medium';
    }

    return '';
  }

  private get tierPrices(): Array<object> {
    if (this.selectedProducts.length !== 1) {
      return [];
    }

    if ( ! this.selectedProducts[0].hasOwnProperty(`tier_price_${this.customerGroupId}`)) {
      return [];
    }

    return JSON.parse(this.selectedProducts[0][`tier_price_${this.customerGroupId}`]);
  }

  private get isInStock(): boolean {
    if (this.selectedProducts && this.selectedProducts.length > 0) {
      return this.selectedProducts[0]['in_stock'];
    }

    return this.productData['in_stock'];
  }

  private changeQty(direction: string): void {
    if (typeof this.qty !== 'string' && this.qty > 0) {
      this.qty = direction === '+' ? this.qty + this.qtyIncrement : direction === '-' && this.qty > 0 ? this.qty - this.qtyIncrement : Math.ceil(this.qty / this.qtyIncrement) * this.qtyIncrement;
      return;
    }

    this.qty = 1;
  }

  private get cartTotals(): number {
    return this.$store.getters['CheckoutTotals/cartTotals'] ?
        this.$store.getters['CheckoutTotals/cartTotals']['subtotal_incl_tax'] :
        0;
  }

  private async productAddToCart(): Promise<void> {
    await this.$store.dispatch('CheckoutTotals/fetchCartTotals');

    this.addToCartIsLoading = true;

    try {
      let productPayload = {
        sku: this.productData['sku'],
        qty: this.qty,
      };

      if (this.productData['product_type'] === 'configurable' && this.selectedOptions.length > 0) {
        const productOptions = {
          product_option: {
            extension_attributes: {
              configurable_item_options: this.selectedOptions.map(option => {
                return {
                  option_id: option['attributeId'],
                  option_value: option['selectedValue']
                }
              })
            }
          }
        }
        productPayload = Object.assign(productPayload, productOptions);
      }
      await this.$store.dispatch('CheckoutCart/addProductToQuote', productPayload);

      if (this.hasAddToCartError) {
        this.addToCartIsLoading = false;
        return;
      }

      this.hasAddToCartSuccessMessage = this.$t('Product has been added to your shopping cart');
      this.hasAddToCartSuccess = true;

      setTimeout(() => {
        this.hasAddToCartSuccess = false;
      }, 5000);
    } catch (e) {
      console.log(e);
      this.hasAddToCartError = true;
      this.addToCartErrorMessage = this.$t('Unable to add product to cart, try again later.');
    } finally {
      this.addToCartIsLoading = false;
      setTimeout(() => {
        this.hasAddToCartError = false;
      }, 5000);
    }
  }

  private get useListSwatch(): boolean {
    return this.$store.getters['ProductView/getUseListSwatch'];
  }

  private created(): void {
    if (this.productData['product_type'] === 'configurable' && Object.keys(this.selectedProduct).length > 0) {
      this.productData['selectedProduct'] = this.selectedProduct;
    }

    this.$store.dispatch('CatalogProduct/getProductData', this.productData);

    const preparedProductData = {...this.productData};
    if (Object.keys(this.selectedProduct).length > 0) {
      preparedProductData['price_incl_tax'] = this.selectedProduct['price_incl_tax'];
      preparedProductData['final_price_incl_tax'] = this.selectedProduct['final_price_incl_tax'];
    }

    SolarDataLayer.init().addViewItem({
      ...preparedProductData,
      'item_id': this.productData['product_id'],
      'item_group_id': this.productData['pim_productfamilie'] || this.productData['product_id'],
      'item_variant': '',
      'item_variant_id': this.productData['product_id'],
    });
  }
}
